// The shared pieces of the territory border (R495, R532): the uniform
// lists and the two GLSL helpers that the outline wash and the outline glow
// of `geo.ts` declare. The border is the traced outline of
// ui-map-interface.md §4.2; the kernel field of R431 that once shared these
// pieces left the tree on 2026-11-03 (R532).

import { TERRITORY_BORDER_PASSES } from '../../gen/presentation';
import { DIST_PLANE_UNIFORMS, type UniformSpec } from './prelude';
import { dashUniforms } from '../../render/faction-marks';

/** The dash table of the faction borders (R1568). */
const DASH_TABLE = dashUniforms();

/**
 * Where the rock mask's field comes from: 0 the kernel, 1 the terrain plane.
 *
 * This is the uniform's value before the layer applies the knob, and it is
 * the fallback the pass keeps while the terrain plane is not there. The
 * plane is the knob's default since R493, and `#rock=kernel` puts the kernel
 * back for a measurement (the frame-throughput report of 2026-09-04,
 * package E).
 */
export const TERRITORY_ROCK_PLANE_OFF = 0;

/**
 * The three-pass glow stroke's figures (R495): the widths, the alphas and
 * the pulse the layer writes every frame.
 */
export const BORDER_PASS_UNIFORMS: readonly UniformSpec[] = [
  { name: 'uPassWidth', type: 'vec3', value: TERRITORY_BORDER_PASSES.map((p) => p.width) },
  { name: 'uPassAlpha', type: 'vec3', value: TERRITORY_BORDER_PASSES.map((p) => p.alpha) },
  { name: 'uPulse', type: 'float', value: 1, dynamic: true, note: '0.75 + 0.25 sin(1.4 t), stepped at TERRITORY_PULSE_HZ' },
];

/**
 * The arrival's territory alpha (R838, R1578). A spawn style that fades
 * the territory in writes it through `TerritoryLayer.setAlpha`, and both
 * programs of `geo.ts` multiply the last colour they write by it, so the
 * wash and the border fade by one factor. At the steady state 1 the
 * product is the colour itself, to the bit.
 */
export const ARRIVAL_UNIFORMS: readonly UniformSpec[] = [
  { name: 'uArrival', type: 'float', value: 1, dynamic: true, note: "the arrival's territory alpha; 1 is the steady state" },
];

/**
 * The dash of a faction border (R1568, R1577). The World field
 * `factionEdge` writes `uEdgeDash`, and the two vec4 lists are the
 * pattern table of `factionmarks.toml`.
 */
export const DASH_UNIFORMS: readonly UniformSpec[] = [
  { name: 'uEdgeDash', type: 'int', value: 1, dynamic: true, note: '1 dashes the line pass by faction' },
  { name: 'uDashA', type: 'vec4[]', value: Array.from(DASH_TABLE.a), note: '8 slots: m0 h0 m1 h1' },
  { name: 'uDashB', type: 'vec4[]', value: Array.from(DASH_TABLE.b), note: '8 slots: m2 h2 period 0' },
];

/**
 * The dash arithmetic along a line (R1568, R1577). The caller declares
 * `DASH_UNIFORMS` and `uWidthScale` in front of it.
 *
 * uDashA[f] is (m0, h0, m1, h1) and uDashB[f] is (m2, h2, period, 0): the
 * middle and the half length of up to three ON runs of one period. A half
 * length under 0 is no run, and a period of 0 is a solid line. `s` is the
 * place along the line in pattern units, which are world px at a width
 * scale of 1. The answer is how far that place stands from the nearest
 * run, in world px: 0 on a run, and the round cap of the stroke follows
 * from it.
 */
export const DASH_ALONG = `
float dashRun(float u, float m, float h, float period) {
  float a = u - m;
  a -= period * floor(a / period + 0.5);
  return (h < 0.0) ? 1e9 : max(abs(a) - h, 0.0);
}

float dashAlong(int f, float s) {
  vec4 b = uDashB[f];
  float period = b.z;
  if (uEdgeDash != 1 || period <= 0.0) return 0.0;
  vec4 a = uDashA[f];
  float u = s - period * floor(s / period);
  float e = min(dashRun(u, a.x, a.y, period),
                min(dashRun(u, a.z, a.w, period), dashRun(u, b.x, b.y, period)));
  return e * uWidthScale;
}
`;

/**
 * The rock mask's terrain-plane branch, and the four uniforms it reads
 * (R495). The outline wash of `geo.ts` is cut by it.
 *
 * The three placement figures are `DIST_PLANE_UNIFORMS`, the terrain
 * layer's own; the territory layer copies them across, so every pass reads
 * one plane through one set of numbers. `uRockPlane` is the measurement
 * knob `#rock` (the frame-throughput report of 2026-09-04, package E).
 */
export const ROCK_PLANE = `
uniform int   uRockPlane;
uniform vec2  uDistLo;
uniform vec2  uDistUv;
uniform vec2  uDistStep;

// The world point p as a uv of the TERRAIN plane (../terrain-prep.ts). It is
// the terrain pass's own helper, written out again here because the two
// passes share no source, only the plane and the three figures that place it.
vec2 distUv(vec2 p) { return (p - uDistLo) * uDistUv; }

// How much of a pixel the ROCK leaves, read off the TERRAIN plane.
//
// It reads fR from the plane's A channel and takes the central difference
// the terrain composite takes, so at terrain=tex the hole edge is the same
// curve as the ridge body edge. uRockPlane 0 answers 1.0 and leaves the
// caller its own mask: the plane lands a moment after the hello, and
// \`#rock=kernel\` selects the kernel for a measurement.
float rockPlaneWater(vec2 p, float px) {
  if (uRockPlane != 1) return 1.0;
  vec2 uv = distUv(p);
  vec2 texel = uDistUv * uDistStep;
  vec2 tx = vec2(texel.x, 0.0), ty = vec2(0.0, texel.y);
  float fRp = texture(uDist, uv).a;
  float xp = texture(uDist, uv + tx).a, xm = texture(uDist, uv - tx).a;
  float yp = texture(uDist, uv + ty).a, ym = texture(uDist, uv - ty).a;
  vec2 dfRp = vec2((xp - xm) / (2.0 * uDistStep.x),
                   (yp - ym) / (2.0 * uDistStep.y));
  float sRp = (fRp - 0.5) / max(length(dfRp), 1e-5);
  return 1.0 - fillCov(sRp, px);
}
`;

/** The four uniforms `ROCK_PLANE` declares. */
export const ROCK_PLANE_UNIFORMS: readonly UniformSpec[] = [
  { name: 'uRockPlane', type: 'int', value: TERRITORY_ROCK_PLANE_OFF },
  ...DIST_PLANE_UNIFORMS,
];
